import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Theme = 'dark' | 'light';

interface UIState {
  sidebarCollapsed: boolean;
  theme: Theme;
  pinnedNavItems: string[];
  recentPages: Array<{ path: string; label: string }>;
  commandPaletteOpen: boolean;
  notificationsOpen: boolean;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  pinNavItem: (path: string) => void;
  unpinNavItem: (path: string) => void;
  addRecentPage: (path: string, label: string) => void;
  setCommandPaletteOpen: (open: boolean) => void;
  setNotificationsOpen: (open: boolean) => void;
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      sidebarCollapsed: false,
      theme: 'dark',
      pinnedNavItems: ['/', '/creators', '/devices'],
      recentPages: [],
      commandPaletteOpen: false,
      notificationsOpen: false,
      toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
      setTheme: (theme) => set({ theme }),
      toggleTheme: () => set((s) => ({ theme: s.theme === 'dark' ? 'light' : 'dark' })),
      pinNavItem: (path) =>
        set((s) => {
          if (s.pinnedNavItems.includes(path)) return s;
          return { pinnedNavItems: [...s.pinnedNavItems, path] };
        }),
      unpinNavItem: (path) =>
        set((s) => ({ pinnedNavItems: s.pinnedNavItems.filter((p) => p !== path) })),
      addRecentPage: (path, label) =>
        set((s) => {
          const filtered = s.recentPages.filter((p) => p.path !== path);
          return { recentPages: [{ path, label }, ...filtered].slice(0, 6) };
        }),
      setCommandPaletteOpen: (open) => set({ commandPaletteOpen: open }),
      setNotificationsOpen: (open) => set({ notificationsOpen: open }),
    }),
    {
      name: 'laxmi-ai-ui',
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        theme: state.theme,
        pinnedNavItems: state.pinnedNavItems,
        recentPages: state.recentPages,
      }),
    },
  ),
);
